// ============================================================
// Auth Store — 登入狀態管理
// ============================================================

import { create } from 'zustand';
import { api } from '../lib/api';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'OWNER' | 'MANAGER' | 'STAFF';
  avatar?: string;
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
}

interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  isLoading: boolean;
  error: string | null;

  login: (email: string, password: string, tenantSlug: string) => Promise<void>;
  logout: () => void;
  restoreSession: () => void;
}

/**
 * 同步從 localStorage 還原登入狀態。
 *
 * ⚠️ 必須在 create() 的初始值就完成，不能等 App 的 useEffect。
 * React 會先執行子元件的 effect：ProtectedLayout 裡的 <Navigate> 會在
 * restoreSession() 之前就把人導去 /pin-login，導致 90 天免登入形同虛設——
 * 每次開啟 App、重新整理、或 iOS 把 PWA 從記憶體回收後回來，都要重打 PIN。
 */
function loadPersistedAuth(): { user: User | null; tenant: Tenant | null } {
  try {
    const token = api.getToken();
    const userStr = localStorage.getItem('ts_user');
    const tenantStr = localStorage.getItem('ts_tenant');
    if (token && userStr && tenantStr) {
      return { user: JSON.parse(userStr), tenant: JSON.parse(tenantStr) };
    }
  } catch {
    // localStorage 讀不到或 JSON 壞掉 → 當作未登入
  }
  return { user: null, tenant: null };
}

const persisted = loadPersistedAuth();

export const useAuthStore = create<AuthState>((set) => ({
  user: persisted.user,
  tenant: persisted.tenant,
  isLoading: false,
  error: null,

  login: async (email, password, tenantSlug) => {
    set({ isLoading: true, error: null });
    try {
      const data = await api.post<{
        token: string;
        user: User;
        tenant: Tenant;
      }>('/auth/login', { email, password, tenantSlug });

      api.setToken(data.token);
      localStorage.setItem('ts_user', JSON.stringify(data.user));
      localStorage.setItem('ts_tenant', JSON.stringify(data.tenant));
      set({ user: data.user, tenant: data.tenant, isLoading: false });
    } catch (err) {
      set({ error: (err as Error).message, isLoading: false });
    }
  },

  logout: () => {
    api.setToken(null);
    localStorage.removeItem('ts_user');
    localStorage.removeItem('ts_tenant');
    // 共用 iPad 上換人登入前，把 Service Worker 快取一併清掉，
    // 避免上一位使用者的頁面資料殘留給下一位。
    if ('caches' in window) {
      caches.keys()
        .then(keys => Promise.all(keys.map(k => caches.delete(k))))
        .catch(() => { /* 清不掉不影響登出 */ });
    }
    set({ user: null, tenant: null });
  },

  restoreSession: () => {
    const token = api.getToken();
    const userStr = localStorage.getItem('ts_user');
    const tenantStr = localStorage.getItem('ts_tenant');

    if (token && userStr && tenantStr) {
      set({
        user: JSON.parse(userStr),
        tenant: JSON.parse(tenantStr),
      });
    }
  },
}));
